import {
  Admin,
  Resource,
  CustomRoutes,
  DashboardComponent,
} from "react-admin";
import { CategoryEdit, CategoryList } from "./modules/categories/categories";
import { ItemEdit, ItemList } from "./modules/items/items";
import {
  CustomerCreate,
  CustomerEdit,
  CustomerList,
} from "./modules/customers/customers";
import { ThemeCreate, ThemeEdit, ThemeList } from "./modules/config/Theme";
import { BrowserRouter, Route, Navigate, useNavigate } from "react-router-dom";
import { Integrate } from "./modules/integration/Integrate";
import {
  LocationCreate,
  LocationEdit,
  LocationList,
} from "./modules/locations/locations";
import CustomLayout from "./components/app/custom.layout";

import TenantIcon from "@mui/icons-material/Apartment";
import BrandIcon from "@mui/icons-material/BroadcastOnPersonal";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import ItemIcon from "@mui/icons-material/ShoppingCart";
import DeviceIcon from "@mui/icons-material/Devices";
import ApplicationIcon from "@mui/icons-material/AppSettingsAlt";
import CaseIcon from "@mui/icons-material/QuestionAnswer";
import CustomerIcon from "@mui/icons-material/Person";
import LocationIcon from "@mui/icons-material/LocationCity";
import ThemeIcon from "@mui/icons-material/ColorLens";
import AIFileIcon from "@mui/icons-material/CloudUpload";
import AIFileStoreIcon from "@mui/icons-material/Folder";
//import AssistantIcon from "@mui/icons-material/AutoAwesome";

import AssistantIcon from "@mui/icons-material/Apartment";


import { AppInitialization } from "./framework/app/app.initialization";
import { RealtimeUpdatesInitializer } from "./components/realtime/RealtimeUpdatesInitializer";
import { AnswerSectionOverlayProvider } from "./modules/ai/chat/AnswerSectionOverlayContext";
import { TenantCreate, TenantList } from "./modules/tenant/tenants";
import { MyTenant } from "./modules/tenant/MyTenant";
import { MyLocation } from "./modules/locations/MyLocation";
import { BrandCreate, BrandEdit, BrandList } from "./modules/brand/brands";
import {
  EnvironmentCreate,
  EnvironmentEdit,
  EnvironmentList,
} from "./modules/developer/Environment";
import { PersonCreate, PersonEdit, PersonList } from "./modules/people/people";
import Analytics from "./modules/analytics/Analytics";
import { useRouteBasedAgenticContext } from "./modules/ai/chat/hooks/useAgenticContext";
import Reports from "./modules/reports/Reports";
import { DeviceCreate, DeviceEdit, DeviceList } from "./modules/devices/devices";
import Catalog from "./modules/catalog/Catalog";
import {
  AppDataContextProvider,
  useAppDataContext,
} from "./framework/app/app.data.context";
import { CaseList } from "./modules/cases/cases";
import AppExtensionProviderRegistry, {
  AppExtension,
} from "./framework/app/app.extension.provider";
import { useEffect, useState } from "react";
import { loadAppConfig } from "./framework/config/app.config";
import LogsResource from "./modules/developer/logs/LogsResource";
import WelcomePage from "./modules/welcome/welcome.page";
import Data from "./modules/data/Data";
import DataManagement from "./modules/data/DataManagement";
import {
  ApplicationCreate,
  ApplicationList,
} from "./modules/application/Application";
import ApplicationEdit from "./modules/application/ApplicationEdit";
import {
  AIAssistantCreate,
  AIAssistantEdit,
  AIAssistantList,
} from "./modules/ai/assistant/AIAssistant";
import TenantEdit from "./modules/tenant/tenant.edit";
import AIOverviewPage from "./modules/ai/overview/ai.overview.page";
import { AIObservatoryView } from "./modules/ai/observatory";
import { ApplicationObservatoryView } from "./modules/applications/observatory";
import {
  AIAssistantInstanceEdit,
  AIAssistantInstanceList,
} from "./modules/ai/assistant/AIAssistantInstance";
import {
  AIFileInstanceCreate,
  AIFileInstanceList,
  AIFileInstanceShow,
} from "./modules/ai/file/AIFileInstance";

import EnvironmentSelector from "./framework/app/environment.selector";
import modernDarkTheme from "./framework/theme/modern.dark.theme";
import { AIFileStoreCreate, AIFileStoreList, AIFileStoreShow } from "./modules/ai/file/AIFileStore";
import { AnalyticsDashboardList, AnalyticsDashboardEdit, AnalyticsDashboardCreate } from './modules/analytics/admin/analytics.dashboard.resource';
import { useDynamicEntities } from "./hooks/useDynamicEntities";
import { useDataModelHierarchy } from "./hooks/useDataModelHierarchy";
import { DynamicEntityList } from "./components/dynamic/DynamicEntityList";
import { DynamicEntityListV2 } from "./components/dynamic/DynamicEntityList.v2";
import { DynamicEntityEditV2 } from "./components/dynamic/DynamicEntityEdit.v2";
import { DynamicEntityCreate } from "./components/dynamic/DynamicEntityCreate";
import { DynamicEntityCreateV2 } from "./components/dynamic/DynamicEntityCreate.v2";
import { DynamicEntityShow } from "./components/dynamic/DynamicEntityShow";
import { DynamicEntityShowV2 } from "./components/dynamic/DynamicEntityShow.v2";
import { resourceRegistry } from "./services/resource-registry";
import { SchemaList, SchemaEdit, SchemaCreate, SchemaShow } from "./modules/schemas/schemas";
import { DataModelList, DataModelEdit, DataModelCreate, DataModelShow } from "./modules/datamodels/datamodels";
import SchemaIcon from "@mui/icons-material/Schema";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import LivePreviewTest from "./modules/live-preview/LivePreviewTest";
import MenuManagementPlaceholder from "./modules/menumanagement/MenuManagement";
import { IngredientsInsights, CostOptimizations, RecommendedOrders } from "./modules/insights";
import { KitchenDisplayConfig, CTDDisplayConfig } from "./modules/configuration";
import { ConnectionList, ConnectionEdit, ConnectionCreate, ConnectionShow } from "./modules/connections/connections";
import { AuthenticationProviderList, AuthenticationProviderEdit, AuthenticationProviderCreate, AuthenticationProviderShow } from "./modules/authentication/authentication-providers";
import CableIcon from "@mui/icons-material/Cable";
import SecurityIcon from "@mui/icons-material/Security";
import { ApiClientInitializer } from "./framework/api/ApiClientInitializer";
import { getBasePath, logRoutingInfo } from "./framework/utils/routing.utils";
import { getConfig } from "./framework/config/app.config";
import { getReturnUrl, clearReturnUrl } from "./framework/authentication/return-url.utils";

export class AppConfig {
  [key: string]: any;
}

const enum LoadingState {
  PreEnvSelected = 'preEnvSelected',
  LoadingAppConfig = 'loadingAppConfig',
  AppInitialized = 'appInitialized'
}

export const App = () => {
  const [apiUrl, setApiUrl] = useState(() => sessionStorage.getItem('selectedEnvUrl') || '');
  const [appConfig, setAppConfig] = useState<AppConfig>();

  const [loadingState, setLoadingState] = useState<LoadingState>(() => {
    const saved = sessionStorage.getItem('loadingState');
    return (saved as LoadingState) || LoadingState.PreEnvSelected;
  });

  const handleEnvironmentSelected = ({ name, url , product}) => {
    console.log('handleEnvironmentSelected', url);
    sessionStorage.setItem('selectedEnvUrl', url);
    sessionStorage.setItem('selectedEnv', name);
    sessionStorage.setItem('selectedEnvProduct', product);
    setApiUrl(url);
    setLoadingState(LoadingState.LoadingAppConfig);
  };

  useEffect(() => {
    sessionStorage.setItem('loadingState', loadingState);
  }, [loadingState]);


  useEffect(() => {

    if (loadingState !== LoadingState.LoadingAppConfig) {
      return;
    }

    const initConfig = async () => {
      await loadAppConfig(apiUrl); // Load app config
      setLoadingState(LoadingState.AppInitialized);
    };

    initConfig();
  }, [loadingState]);

  /*
  useEffect(() => {
    console.log('serviceWorker:loadingState', loadingState);
    if (loadingState !== LoadingState.AppInitialized) {
      console.log('not init service worker');
      return;
    }

    

    if ("serviceWorker" in navigator) {
      const env = sessionStorage.getItem('selectedEnv');

      console.log('registering service worker in app load:', env);

      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register(`/service-worker.js?v=${env}`)
          .then((registration) => {
            console.log(
              "Service Worker registered with scope:",
              registration.scope
            );
            console.log(
              "Service Worker registered with env:",
              env
            );
          })
          .catch((error) => {
            console.error("Service Worker registration failed:", error);
          });
      });
    }
  }, [loadingState]);
  */


  if (loadingState === LoadingState.PreEnvSelected) {
    return <EnvironmentSelector onEnvironmentSelected={handleEnvironmentSelected} />;
  }

  if (loadingState === LoadingState.LoadingAppConfig) {
    return <div>Initializing...</div>;
  }

  console.log('App: apiUrl', apiUrl);
  
  // Detect base path for proper routing in subdirectory deployments
  const basePath = getBasePath();
  logRoutingInfo();
  
  AppInitialization.initAppContext();

  return (
    <BrowserRouter basename={basePath}>
      <AppDataContextProvider apiUrl={apiUrl}>
        <AppWithContext />
      </AppDataContextProvider>
    </BrowserRouter>
  );
};

const AppWithContext = () => {
  console.log('🧪 AppWithContext: Component starting...');
  
  const navigate = useNavigate();
  const { appContext } = useAppDataContext();

  // Route-based AI chat context management
  // This automatically sets the correct feature/application context based on current route
  // Individual components can still override with useAgenticContext hook if needed
  useRouteBasedAgenticContext({
    // Analytics section - use analytics-dashboard feature
    '/analytics': { 
      featureKey: 'analytics-dashboard',
      clearOnUnmount: true 
    },
    // Analytics dashboard admin pages
    '/analytics-dashboards': { 
      featureKey: 'analytics-dashboard',
      clearOnUnmount: true 
    },
    '/analytics-dashboards/:id': { 
      featureKey: 'analytics-dashboard',
      clearOnUnmount: true 
    },
    // Reports section - use reports-dashboard feature
    '/reports': { 
      featureKey: 'reports-dashboard',
      clearOnUnmount: true 
    },
    // Developer/Memory Analysis section
    '/developer': (params) => ({
      featureKey: 'memory-analysis',
      clearOnUnmount: true,
    }),
    // Applications section - use application-ui-builder feature
    '/applications/:id': (params) => ({
      featureKey: 'application-ui-builder',
      focusKey: `application:${params.id}`,
      clearOnUnmount: true,
    }),
  });
  
  // Check if this is mmadmin environment (multi-tenant admin)
  const selectedEnv = sessionStorage.getItem('selectedEnv');
  const envLower = selectedEnv?.toLowerCase().replace(/[-_\s]/g, '') || '';
  const baseProduct = sessionStorage.getItem('selectedEnvProduct') || getConfig('VITE_APP_PRODUCT') || 'neo';

  const parseBoolean = (value: any) => value === true || value === 'true';
  const isDefaultEnvMode = parseBoolean(getConfig('VITE_DEFAULT_ENVIRONMENT'));

  const [isTenantAdmin, setIsTenantAdmin] = useState(() => sessionStorage.getItem('isTenantAdmin') === 'true');
  const [adminContextLoaded, setAdminContextLoaded] = useState(!isDefaultEnvMode);
  const [hasLocationContext, setHasLocationContext] = useState(false);
  const [hasNavigatedToReturnUrl, setHasNavigatedToReturnUrl] = useState(false);

  const legacyIsMMAdmin = envLower.includes('admin') || envLower.includes('madmobile');
  const showAdminFeatures = isDefaultEnvMode ? isTenantAdmin : legacyIsMMAdmin;
  
  // Load dynamic resources immediately so hook order stays stable
  const { entities: dynamicEntities, loading: entitiesLoading } = useDynamicEntities();
  const { domains: dataModelDomains, loading: dataModelLoading } = useDataModelHierarchy();
  
  useEffect(() => {
    if (!appContext?.tokenProvider) {
      return;
    }

    let mounted = true;

    const resolveTenantInfo = async () => {
      try {
        const token = await appContext.tokenProvider.getIdToken();
        const apiUrl = sessionStorage.getItem('selectedEnvUrl') || window.location.origin;
        const url = `${apiUrl}/api/tenant-info/current`;
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (mounted) {
            const adminFlag = !!data.isMultiTenantAdmin;
            setIsTenantAdmin(adminFlag);
            sessionStorage.setItem('isTenantAdmin', String(adminFlag));
          }
        } else {
          console.log('ℹ️ Unable to load tenant info (status):', response.status);
        }
      } catch (err) {
        console.log('ℹ️ No tenant context available yet', err);
      } finally {
        if (mounted && isDefaultEnvMode) {
          setAdminContextLoaded(true);
        }
      }
    };

    resolveTenantInfo();

    return () => {
      mounted = false;
    };
  }, [appContext, isDefaultEnvMode]);

  useEffect(() => {
    const determineLocationContext = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const token = await (window as any).authProvider?.getIdToken();
        const apiUrl = sessionStorage.getItem('selectedEnvUrl') || window.location.origin;
        const url = `${apiUrl}/api/location-info/current`;
        console.log('🔍 Checking for location context...', { apiUrl, url, hasToken: !!token });

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('📡 Location check response:', response.status, response.ok);

        if (response.ok) {
          const data = await response.json();
          const hasLocation = !!data.locationId;
          setHasLocationContext(hasLocation);
          sessionStorage.setItem('hasLocationContext', String(hasLocation));
          console.log('🔐 Location context detected:', hasLocation, 'locationId:', data.locationId);
        } else {
          console.log('ℹ️ No location context (status:', response.status, ')');
          sessionStorage.setItem('hasLocationContext', 'false');
        }
      } catch (err) {
        console.log('ℹ️ No location context available (this is normal for tenant/brand environments)', err);
        sessionStorage.setItem('hasLocationContext', 'false');
      }
    };

    if (!showAdminFeatures) {
      determineLocationContext();
    }
  }, [showAdminFeatures]);

  useEffect(() => {
    const activeProduct = showAdminFeatures ? 'mmadmin' : (baseProduct || 'neo');
    sessionStorage.setItem('activeAppProduct', activeProduct);
  }, [showAdminFeatures, baseProduct]);

  // Handle return URL after authentication (deep linking)
  useEffect(() => {
    if (hasNavigatedToReturnUrl || !appContext) {
      return;
    }

    // Check for return URL from session storage
    const returnUrl = getReturnUrl();
    if (returnUrl) {
      console.log('[ReturnURL] Found stored return URL, navigating to:', returnUrl);
      setHasNavigatedToReturnUrl(true);
      clearReturnUrl();
      navigate(returnUrl, { replace: true });
    }
  }, [appContext, hasNavigatedToReturnUrl, navigate]);

  console.log('🔐 Environment check:', {
    selectedEnv,
    envLower,
    showAdminFeatures,
    isDefaultEnvMode,
    hasLocationContext
  });
  
  let baseExtension: AppExtension | undefined;
  try {
    baseExtension = AppExtensionProviderRegistry.getProvider(baseProduct)?.getAppExtension();
  } catch (err) {
    console.warn('⚠️ Unable to load base extension provider', baseProduct, err);
  }

  let adminExtension: AppExtension | undefined;
  if (showAdminFeatures) {
    try {
      adminExtension = AppExtensionProviderRegistry.getProvider('mmadmin')?.getAppExtension();
    } catch (err) {
      console.warn('⚠️ Unable to load admin extension provider', err);
    }
  }

  const extensionRoutes = [
    ...(baseExtension?.getRoutes?.() || []),
    ...(adminExtension?.getRoutes?.() || [])
  ];
  const welcomePage: DashboardComponent =
    adminExtension?.getWelcomePage?.() ||
    baseExtension?.getWelcomePage?.() ||
    WelcomePage;

  // 🚀 Trigger analytics preloading on app startup (inside Admin component context)
  // This is wrapped in a component below to ensure dataProvider is available

  console.log('🧪 AppWithContext: appContext status:', !!appContext);
  console.log('🧪 AppWithContext: dataModelDomains:', { 
    loading: dataModelLoading, 
    count: dataModelDomains?.length || 0,
    totalSchemas: dataModelDomains?.reduce((sum, d) => sum + d.schemas.length, 0) || 0
  });
  console.log('🧪 AppWithContext: dynamicEntities status:', { 
    loading: entitiesLoading, 
    count: dynamicEntities?.length || 0 
  });

  if (isDefaultEnvMode && !adminContextLoaded) {
    return <div>Initializing role-based environment...</div>;
  }

  if (!appContext) {
    console.log('🧪 AppWithContext: No appContext, showing initializing...');
    return <div>Initializing...</div>;
  }

  if (entitiesLoading) {
    console.log('🧪 AppWithContext: Loading dynamic entities...');
    return <div>Loading dynamic resources...</div>;
  }

  console.log('🧪 AppWithContext: Rendering Admin component with resources...');

  return (
    <>
      {/* Initialize ApiClient with auth provider - OUTSIDE of <Admin> to ensure it renders */}
      <ApiClientInitializer tokenProvider={appContext.tokenProvider} />
      
      {/* Wrap Admin with AnswerSectionOverlayProvider for inline chat overlay support */}
      <AnswerSectionOverlayProvider>
        <Admin
        authProvider={appContext.authProvider}
        dataProvider={appContext.dataProvider}
        loginPage={false}
        layout={CustomLayout}
        dashboard={welcomePage}
        theme={modernDarkTheme }
      >
      
      {/* Conditional Tenant Resource based on environment */}
      {showAdminFeatures ? (
        // Mad Mobile Admin - Show full tenant management
        <Resource
          name="tenant"
          list={TenantList}
          edit={TenantEdit}
          create={TenantCreate}
          icon={TenantIcon}
          options={{ label: "Tenants" }}
        />
      ) : (
        // Tenant/Brand/Location instance - Hide tenant list, only allow editing own tenant
        <Resource
          name="tenant"
          edit={TenantEdit}
          icon={TenantIcon}
          options={{ label: "Tenants" }}
        />
      )}
      {/* Brand and Location are managed through Tenant editor, hide from menu */}
      <Resource
        name="brand"
        edit={BrandEdit}
        create={BrandCreate}
        options={{ label: "Brands" }}
      />
      <Resource
        name="location"
        list={LocationList}
        create={LocationCreate}
        edit={LocationEdit}
        options={{ label: "Locations" }}
      />
      <Resource
        name="person"
        list={PersonList}
        edit={PersonEdit}
        create={PersonCreate}
        icon={PersonIcon}
        options={{ label: "Team" }}
      />
      <Resource
        name="category"
        list={CategoryList}
        edit={CategoryEdit}
        icon={CategoryIcon}
        options={{ label: "Categories" }}
      />
      <Resource
        name="item"
        list={ItemList}
        edit={ItemEdit}
        icon={ItemIcon}
        options={{ label: "Items" }}
      />
      <Resource
        name="customer"
        list={CustomerList}
        edit={CustomerEdit}
        create={CustomerCreate}
        icon={CustomerIcon}
        options={{ label: "Customers" }}
      />
      <Resource
        name="device"
        list={DeviceList}
        create={DeviceCreate}
        edit={DeviceEdit}
        icon={DeviceIcon}
        options={{ label: "Devices" }}
      />
      <Resource
        name="case"
        list={CaseList}
        icon={CaseIcon}
        options={{ label: "Support" }}
      />
      <Resource
        name="theme"
        list={ThemeList}
        edit={ThemeEdit}
        create={ThemeCreate}
        icon={ThemeIcon}
        options={{ label: "Theme" }}
      />
      <Resource
        name="environment"
        list={EnvironmentList}
        edit={EnvironmentEdit}
        create={EnvironmentCreate}
        icon={ThemeIcon}
        options={{ label: "Environment" }}
      />
      <Resource
        name="application"
        list={ApplicationList}
        edit={ApplicationEdit}
        create={ApplicationCreate}
        icon={ApplicationIcon}
        options={{ label: "Applications" }}
      />
      <Resource
        name="aiassistant"
        list={AIAssistantList}
        edit={AIAssistantEdit}
        create={AIAssistantCreate}
        icon={AssistantIcon}
        options={{ label: "AI Assistants" }}
      />
      <Resource
        name="aiassistantinstance"
        list={AIAssistantInstanceList}
        edit={AIAssistantInstanceEdit}
        icon={AssistantIcon}
        options={{ label: "AI Instances" }}
      />
      <Resource
        name="aifileinstance"
        list={AIFileInstanceList}
        show={AIFileInstanceShow}
        create={AIFileInstanceCreate}
        icon={AIFileIcon}
        options={{ label: "AI Files" }}
      />
       <Resource
        name="aifilestore"
        list={AIFileStoreList}
        show={AIFileStoreShow}
        create={AIFileStoreCreate}
        icon={AIFileStoreIcon}
        options={{ label: "AI File Stores" }}
      />
      <Resource
        name="integration"
        icon={AIFileIcon}
        options={{ label: "Integrations" }}
      />
      <Resource
        name="analytics-dashboards"
        list={AnalyticsDashboardList}
        edit={AnalyticsDashboardEdit}
        create={AnalyticsDashboardCreate}
        options={{ label: 'Dashboards' }}
      />
      <Resource
        name="schema"
        list={SchemaList}
        show={SchemaShow}
        edit={SchemaEdit}
        create={SchemaCreate}
        icon={SchemaIcon}
        options={{ label: 'Schemas' }}
      />
      <Resource
        name="datamodel"
        list={DataModelList}
        show={DataModelShow}
        edit={DataModelEdit}
        create={DataModelCreate}
        icon={SchemaIcon}
        options={{ label: 'Data Models' }}
      />
      <Resource
        name="connection"
        list={ConnectionList}
        show={ConnectionShow}
        edit={ConnectionEdit}
        create={ConnectionCreate}
        icon={CableIcon}
        options={{ label: 'Connections' }}
      />
      <Resource
        name="authentication-provider"
        list={AuthenticationProviderList}
        show={AuthenticationProviderShow}
        edit={AuthenticationProviderEdit}
        create={AuthenticationProviderCreate}
        icon={SecurityIcon}
        options={{ label: 'Authentication' }}
      />
      <LogsResource />
      
      {/* Dynamic Resources from DataModels (consumer, invoice, product, etc.) */}
      {!dataModelLoading && dataModelDomains.flatMap(domain => domain.schemas)
        .filter(schema => {
          // Exclude static resources that have their own controllers
          // AND framework entities (Location, Brand, Device, Tenant)
          const excludedSchemas = [
            'connection', 
            'authentication-provider',
            'location',      // Framework entity
            'brand',         // Framework entity
            'device',        // Framework entity
            'tenant'         // Framework entity
          ];
          return !excludedSchemas.includes(schema.key);
        })
        .map(schema => {
        const schemaKey = schema.key;
        const schemaName = schema.name;
        
        console.log(`🎨 Creating DataModel Resource for: ${schemaKey}`);
        
        const entityMetadata = {
          name: schemaName,
          resourceName: schemaKey,
          fields: [],
          hasRestController: true
        };
        
        return (
          <Resource
            key={`datamodel-${schemaKey}`}
            name={schemaKey}
            list={() => {
              console.log(`📋 List for DataModel entity: ${schemaKey}`);
              return <DynamicEntityListV2 schemaKey={schemaKey} entityName={schemaName} />;
            }}
            edit={() => {
              console.log(`✏️ Edit for DataModel entity: ${schemaKey}`);
              return <DynamicEntityEditV2 entityMetadata={entityMetadata as any} schemaKey={schemaKey} />;
            }}
            create={() => {
              console.log(`➕ Create for DataModel entity: ${schemaKey}`);
              return <DynamicEntityCreateV2 entityMetadata={entityMetadata as any} schemaKey={schemaKey} />;
            }}
            show={() => {
              console.log(`👁️ Show for DataModel entity: ${schemaKey}`);
              return <DynamicEntityShowV2 entityMetadata={entityMetadata as any} schemaKey={schemaKey} entityName={schemaName} />;
            }}
            icon={SchemaIcon}
            options={{ label: schemaName }}
          />
        );
      })}
      
      {/* Dynamic Resources from Entity Discovery (platform entities) */}
      {dynamicEntities
        .filter(entity => {
          // Exclude DataModel entities (they have dataModelKey or source="generated")
          const isFromDataModel = !!entity.metadata.dataModelKey || entity.metadata.source === 'generated';
          if (isFromDataModel) {
            console.log(`⏭️ Skipping ${entity.resourceName} - from DataModel (handled by inline resources above)`);
            return false;
          }
          
          // Exclude manually-defined resources with custom components & icons
          // These are defined earlier in App.tsx with specific List/Edit/Create components
          const hasCustomComponents = [
            'application',      // ApplicationList, ApplicationEdit, ApplicationCreate
            'environment',      // EnvironmentList, EnvironmentEdit, EnvironmentCreate
            'aiassistant',      // AIAssistantList, AIAssistantEdit, AIAssistantCreate
            'aiassistantinstance', // AIAssistantInstanceList, AIAssistantInstanceEdit
            'aifileinstance',   // AIFileInstanceList, AIFileInstanceShow, AIFileInstanceCreate
            'aifilestore',      // AIFileStoreList, AIFileStoreShow, AIFileStoreCreate
            'tenant',           // TenantCreate, TenantEdit, TenantList
            'brand',            // BrandCreate, BrandEdit, BrandList
            'analyticsdashboard', // AnalyticsDashboardList, AnalyticsDashboardEdit
          ].includes(entity.resourceName);
          
          if (hasCustomComponents) {
            console.log(`⏭️ Skipping ${entity.resourceName} - has custom components with icon`);
            return false;
          }
          
          // Include all other framework/platform entities
          console.log(`✅ Including ${entity.resourceName} for dynamic resource`);
          return true;
        })
        .map(entity => {
          console.log(`🔧 Creating resource for: ${entity.name} -> ${entity.resourceName}`);
          return (
            <Resource
              key={entity.name}
              name={entity.resourceName}
              list={() => <DynamicEntityList entityMetadata={entity.metadata} />}
              edit={() => {
                const schemaKey = resourceRegistry.getSchemaKey(entity.resourceName);
                console.log(`🔧 Creating edit component for ${entity.resourceName}, schemaKey: ${schemaKey}`);
                return <DynamicEntityEditV2 entityMetadata={entity.metadata} schemaKey={schemaKey} />;
              }}
              create={() => <DynamicEntityCreate entityMetadata={entity.metadata} />}
              show={() => {
                const schemaKey = resourceRegistry.getSchemaKey(entity.resourceName);
                return <DynamicEntityShowV2 entityMetadata={entity.metadata} schemaKey={schemaKey} entityName={entity.label} />;
              }}
              options={{ label: entity.label }}
            />
          );
        })}
      
      {/* 
        NOTE: GeneratedEntityResourceProvider and DataModelResourceProvider removed!
        All schemas are now handled inline above (lines 457-500), which includes:
        - Schemas assigned to domains
        - Orphaned schemas (automatically added to "General" domain)
        This prevents duplicate Resource registration.
      */}
      
      <CustomRoutes>
        <Route path="/my-tenant" element={<MyTenant />} />
        <Route path="/my-location" element={<MyLocation />} />
        <Route path="/catalog/*" element={<Catalog />} />
        <Route path="/compose" element={<Integrate />} />
        <Route path="/developer" element={<EnvironmentList />} />
        <Route path="/ai" element={<AIOverviewPage />} />
        <Route path="/ai/observatory" element={<AIObservatoryView />} />
        <Route path="/applications/observatory" element={<ApplicationObservatoryView />} />
        <Route path="/data/*" element={<DataManagement />} />
        <Route path="/data-legacy" element={<Data />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/menumanagement" element={<MenuManagementPlaceholder />} />
        <Route path="/insights/ingredients" element={<IngredientsInsights />} />
        <Route path="/insights/cost-optimizations" element={<CostOptimizations />} />
        <Route path="/insights/recommended-orders" element={<RecommendedOrders />} />
        <Route path="/configuration/kitchen-display" element={<KitchenDisplayConfig />} />
        <Route path="/configuration/ctd-display" element={<CTDDisplayConfig />} />
        <Route path="/live-preview-test" element={<LivePreviewTest />} />

      </CustomRoutes>
      </Admin>
      </AnswerSectionOverlayProvider>
    </>
  );
};
